import { useState } from 'react';

import { CircleLoader } from '@proton/atoms/CircleLoader/CircleLoader';
import { FileIcon, Radio, useErrorHandler } from '@proton/components';
import { IcChevronUp } from '@proton/icons/icons/IcChevronUp';
import { IcFile } from '@proton/icons/icons/IcFile';
import { IcInbox } from '@proton/icons/icons/IcInbox';
import { IcTv } from '@proton/icons/icons/IcTv';
import { IcUsers } from '@proton/icons/icons/IcUsers';
import clsx from '@proton/utils/clsx';
import orderBy from '@proton/utils/orderBy';

import './DirectoryTree.scss';

export interface DirectoryTreeItem {
    nodeUid: string;
    treeItemId: string;
    name: string;
    expandable: boolean;
    type: 'files-root' | 'devices-root' | 'device' | 'shares-root' | 'folder' | string;
    children: Record<string, DirectoryTreeItem> | null;
}

type SelectItemFunction = (selected: string, item: any) => void;
type ToggleExpandFunction = (treeItemId: string) => Promise<void>;

export function DirectoryTreeRoot({
    roots,
    toggleExpand,
    selectedTreeId,
    onSelect,
}: {
    roots: DirectoryTreeItem[];
    toggleExpand: ToggleExpandFunction;
    selectedTreeId?: string;
    onSelect: SelectItemFunction;
}) {
    if (!roots) {
        return null;
    }

    return (
        <div className="border rounded" style={{ position: 'relative' }}>
            <ul data-testid="directorytree-root" className="unstyled mt-0">
                {roots.map((root) => (
                    <DirectoryTreeBranch
                        key={root.treeItemId}
                        item={root}
                        toggleExpand={toggleExpand}
                        selectedTreeId={selectedTreeId}
                        onSelect={onSelect}
                    />
                ))}
            </ul>
        </div>
    );
}

function DirectoryTree({
    tree,
    toggleExpand,
    selectedTreeId,
    onSelect,
}: {
    tree: Record<string, DirectoryTreeItem>;
    toggleExpand: ToggleExpandFunction;
    selectedTreeId?: string;
    onSelect: SelectItemFunction;
}) {
    return (
        <ul className="unstyled mt-0">
            {orderBy(Object.values(tree), 'name').map((item) => (
                <DirectoryTreeBranch
                    key={item.treeItemId}
                    item={item}
                    toggleExpand={toggleExpand}
                    selectedTreeId={selectedTreeId}
                    onSelect={onSelect}
                />
            ))}
        </ul>
    );
}

function DirectoryTreeBranch({
    item,
    toggleExpand,
    selectedTreeId,
    onSelect,
}: {
    item: DirectoryTreeItem;
    toggleExpand: ToggleExpandFunction;
    selectedTreeId?: string;
    onSelect: SelectItemFunction;
}) {
    const handleError = useErrorHandler();

    const [isLoading, setIsLoading] = useState(false);
    const handleExpand = () => {
        setIsLoading(true);
        toggleExpand(item.treeItemId)
            .catch(handleError)
            .finally(() => setIsLoading(false));
    };

    const isSelected = selectedTreeId === item.treeItemId;
    const handleSelect = () => onSelect(item.treeItemId, item);

    const isCollapsed = item.children === null;
    return (
        <li data-testid="directorytree-item" className="pl-4">
            {/* Keyboard accessibility is provided by the Radio component inside, so no need for onKeyDown here */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div className="directory-tree-node flex items-center p-2 cursor-pointer" onClick={handleSelect}>
                <Radio
                    id={item.treeItemId}
                    name={item.treeItemId}
                    onChange={handleSelect}
                    aria-selected={isSelected}
                    checked={isSelected}
                    className={clsx('absolute left-0 ml-2', !isSelected && 'opacity-0')}
                    data-testid={isSelected ? 'directorytree-item-selected' : 'directorytree-item-not-selected'}
                />

                <div className="flex items-center gap-4 pl-6">
                    <TreeItemIcon type={item.type} />

                    {item.expandable && (
                        <button
                            className={clsx('flex', isCollapsed ? 'rotateX-180' : '')}
                            onClick={handleExpand}
                            disabled={isLoading}
                            type="button"
                            aria-expanded={isCollapsed ? 'false' : 'true'}
                        >
                            <IcChevronUp className="border border-norm rounded-50" />
                        </button>
                    )}
                    <div className="flex-1 min-w-0 text-ellipsis" data-testid="directorytree-item-name">
                        {item.name}
                    </div>
                </div>
            </div>

            {/* Border: can't be done with normal CSS because parent element visually indent the component */}
            <div className="absolute left-0 w-full h-px bg-strong"></div>

            {isLoading && (
                <div className="p-2 w-full text-center">
                    <CircleLoader />
                </div>
            )}

            {item.children !== null && (
                <DirectoryTree
                    tree={item.children}
                    toggleExpand={toggleExpand}
                    selectedTreeId={selectedTreeId}
                    onSelect={onSelect}
                />
            )}
        </li>
    );
}

function TreeItemIcon({ type }: { type: string }) {
    if (type === 'files-root') {
        return <IcInbox />;
    }
    if (type === 'devices-root' || type === 'device') {
        return <IcTv />;
    }
    if (type === 'shares-root') {
        return <IcUsers />;
    }
    if (type === 'folder') {
        return <FileIcon mimeType={'Folder'} />;
    }
    return <IcFile />;
}
